/**
 * HomeClassicsRail — Indian Classical Hits rail with decade-filter tabs.
 *
 * Renders the curated 50s-80s catalog with pill tabs above the carousel:
 *   • All  ·  50s  ·  60s  ·  70s  ·  80s
 *
 * Decade filter is client-side (data already has `release_year`), so tab
 * switches are instant — no extra fetch. Empty decades are hidden as tabs.
 *
 * Subtitle updates contextually per decade for editorial flavor.
 */
import { useMemo, useState } from "react";
import MovieCarousel from "./MovieCarousel";

const DECADE_TABS = [
  { key: "all",  label: "All",  min: 1900, max: 2099, subtitle: "The Golden-era of Hindi cinema." },
  { key: "50s",  label: "50s",  min: 1950, max: 1959, subtitle: "Post-independence cinema — Bimal Roy, Raj Kapoor, Guru Dutt & the Golden Age foundations" },
  { key: "60s",  label: "60s",  min: 1960, max: 1969, subtitle: "Lavish romances & noir thrillers — Mughal-e-Azam, Guide, Jewel Thief" },
  { key: "70s",  label: "70s",  min: 1970, max: 1979, subtitle: "Hrishikesh Mukherjee comedies + the rise of Amitabh — Anand, Sholay, Deewaar" },
  { key: "80s",  label: "80s",  min: 1980, max: 1989, subtitle: "Last gasp of classical era — Masoom, Mr. India, Saaransh, Tezaab" },
];

export default function HomeClassicsRail({ items }) {
  const [tab, setTab] = useState("all");

  // Available decades from data — hide tabs with zero items
  const decadeCounts = useMemo(() => {
    const counts = { all: items.length, "50s": 0, "60s": 0, "70s": 0, "80s": 0 };
    for (const it of items) {
      const y = it.release_year;
      if (!y) continue;
      if (y >= 1950 && y < 1960) counts["50s"]++;
      else if (y >= 1960 && y < 1970) counts["60s"]++;
      else if (y >= 1970 && y < 1980) counts["70s"]++;
      else if (y >= 1980 && y < 1990) counts["80s"]++;
    }
    return counts;
  }, [items]);

  const filtered = useMemo(() => {
    const t = DECADE_TABS.find((d) => d.key === tab) || DECADE_TABS[0];
    if (t.key === "all") return items;
    return items.filter((it) => it.release_year >= t.min && it.release_year <= t.max);
  }, [items, tab]);

  const currentSubtitle = (DECADE_TABS.find((d) => d.key === tab) || DECADE_TABS[0]).subtitle;

  if (!items || items.length === 0) return null;

  return (
    <div data-testid="home-classics-rail">
      {/* Decade tabs — small pills above the rail header. Placed inline so the
          MovieCarousel header still shows the title cleanly. */}
      <div className="px-6 lg:px-10 max-w-[1440px] mx-auto -mb-2 pt-2 flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-white/40 mr-1">Era:</span>
        {DECADE_TABS.map((d) => {
          const count = decadeCounts[d.key] || 0;
          if (d.key !== "all" && count === 0) return null;
          const active = tab === d.key;
          return (
            <button
              key={d.key}
              onClick={() => setTab(d.key)}
              data-testid={`classics-tab-${d.key}`}
              className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                active
                  ? "bg-white text-black"
                  : "bg-white/[0.06] text-white/70 hover:bg-white/[0.12] hover:text-white"
              }`}
            >
              {d.label}
              {d.key !== "all" && (
                <span className={`ml-1.5 text-[10px] ${active ? "text-black/60" : "text-white/40"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <MovieCarousel
        title="Indian Classical Hits"
        items={filtered}
        testid="row-indian-classics"
        viewAllHref="/list/indian-classical-hits"
      />
    </div>
  );
}
